import { z } from "zod";
import {
	batchSourceSchema,
	batchStatusSchema,
	dietSchema,
	kotKindSchema,
	kotStatusSchema,
	orderItemStatusSchema,
	orderStatusSchema,
	orderTypeSchema,
	serviceRequestStatusSchema,
	serviceRequestTypeSchema,
} from "./enums";
import { lineOfferSchema } from "./offers";
import { kotLineSchema, orderItemModifierSchema } from "./ordering";
import { optionalNote, orderLineInputSchema, orderTotalsSchema } from "./ordering-public";
import { phoneSchema } from "./tenancy";

/**
 * Staff ordering: `staff.ordering.*`. See docs/architecture/api.md, Ordering (staff).
 */

const reason = z.string().trim().min(1).max(200);
const lines = z.array(orderLineInputSchema).min(1).max(100);

// ---------------------------------------------------------------------------
// Outputs.
// ---------------------------------------------------------------------------

export const orderItemSchema = z.object({
	id: z.uuid(),
	batchId: z.uuid(),
	/** The menu item, or null if it was deleted since. */
	itemId: z.uuid().nullable(),
	variantId: z.uuid().nullable(),
	name: z.string(),
	/** For KOTs and bills; null prints `name`. */
	namePrint: z.string().nullable(),
	variantName: z.string().nullable(),
	diet: dietSchema,
	modifiers: z.array(orderItemModifierSchema),
	unitPricePaise: z.number().int(),
	modifiersTotalPaise: z.number().int(),
	quantity: z.number().int(),
	/** (unit + modifiers) × quantity. */
	lineTotalPaise: z.number().int(),
	/** The item offer it was ordered under: so much off each unit (P9-T04). */
	offer: lineOfferSchema.nullable(),
	taxRateBp: z.number().int(),
	note: z.string().nullable(),
	kitchenStationId: z.uuid().nullable(),
	status: orderItemStatusSchema,
	cancelReason: z.string().nullable(),
	cancelledAt: z.iso.datetime().nullable(),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

/** What the order card shows about the diner who placed a QR batch. */
export const batchGuestSchema = z.object({
	sessionId: z.uuid(),
	name: z.string().nullable(),
	phone: z.string().nullable(),
	blocked: z.boolean(),
	/** This is the session's first batch: a new diner. */
	firstBatch: z.boolean(),
	/** Batches of this session rejected before. */
	rejectedBefore: z.number().int(),
});
export type BatchGuest = z.infer<typeof batchGuestSchema>;

export const batchSchema = z.object({
	id: z.uuid(),
	/** Null while a QR batch waits for a decision. */
	orderId: z.uuid().nullable(),
	tableId: z.uuid().nullable(),
	tableLabel: z.string().nullable(),
	/** 1, 2, 3 within the order; null until it joins one. */
	seq: z.number().int().nullable(),
	source: batchSourceSchema,
	status: batchStatusSchema,
	note: z.string().nullable(),
	createdAt: z.iso.datetime(),
	/** Staff who added a staff batch. */
	placedBy: z.uuid().nullable(),
	decidedBy: z.uuid().nullable(),
	decidedAt: z.iso.datetime().nullable(),
	rejectReason: z.string().nullable(),
	/** For QR batches. */
	guest: batchGuestSchema.nullable(),
	items: z.array(orderItemSchema),
});
export type Batch = z.infer<typeof batchSchema>;

export const kotSchema = z.object({
	id: z.uuid(),
	orderId: z.uuid(),
	batchId: z.uuid().nullable(),
	kitchenStationId: z.uuid().nullable(),
	stationName: z.string(),
	tableLabel: z.string().nullable(),
	/** Running number per business date. */
	kotNo: z.number().int(),
	businessDate: z.string(),
	/** `cancel`: print as a CANCEL ticket. */
	kind: kotKindSchema,
	status: kotStatusSchema,
	lines: z.array(kotLineSchema),
	createdAt: z.iso.datetime(),
	doneAt: z.iso.datetime().nullable(),
	/** The order's number, the takeaway token. */
	orderNo: z.number().int(),
});
export type Kot = z.infer<typeof kotSchema>;

export const orderSchema = z.object({
	id: z.uuid(),
	/** Running number per business date; the token of a takeaway. */
	orderNo: z.number().int(),
	businessDate: z.string(),
	type: orderTypeSchema,
	tableId: z.uuid().nullable(),
	tableLabel: z.string().nullable(),
	status: orderStatusSchema,
	covers: z.number().int().nullable(),
	customerName: z.string().nullable(),
	customerPhone: z.string().nullable(),
	notes: z.string().nullable(),
	openedAt: z.iso.datetime(),
	openedBy: z.uuid().nullable(),
	closedAt: z.iso.datetime().nullable(),
	cancelReason: z.string().nullable(),
	/** Set when merged into another order (then `cancelled`). */
	mergedIntoId: z.uuid().nullable(),
	/** The order's batches by `seq`, with their items, removed and cancelled ones included. */
	batches: z.array(batchSchema),
	/** Newest first. */
	kots: z.array(kotSchema),
	/** Preview from billing-core over the active items; no discounts yet. */
	totals: orderTotalsSchema,
});
export type Order = z.infer<typeof orderSchema>;

/** A card on the live board and a tile on the Tables tab. */
export const liveOrderSchema = z.object({
	id: z.uuid(),
	orderNo: z.number().int(),
	type: orderTypeSchema,
	tableId: z.uuid().nullable(),
	tableLabel: z.string().nullable(),
	status: z.enum(["open", "billed"]),
	openedAt: z.iso.datetime(),
	covers: z.number().int().nullable(),
	customerName: z.string().nullable(),
	/** Active items, counting quantities. */
	itemCount: z.number().int(),
	totalPaise: z.number().int(),
	lastBatchAt: z.iso.datetime().nullable(),
	/**
	 * A diner tapped "I have paid" (P4-T14): when, and the balance then. Staff
	 * confirm by recording the payment, which clears it (P4-T22's alert).
	 */
	paymentClaim: z
		.object({ at: z.iso.datetime(), amountPaise: z.number().int() })
		.nullable()
		.optional(),
});
export type LiveOrder = z.infer<typeof liveOrderSchema>;

export const serviceRequestSchema = z.object({
	id: z.uuid(),
	tableId: z.uuid(),
	tableLabel: z.string().nullable(),
	orderId: z.uuid().nullable(),
	type: serviceRequestTypeSchema,
	status: serviceRequestStatusSchema,
	/** Taps, merged ones included. */
	count: z.number().int(),
	createdAt: z.iso.datetime(),
	lastRequestedAt: z.iso.datetime(),
	acknowledgedBy: z.uuid().nullable(),
	acknowledgedAt: z.iso.datetime().nullable(),
	resolvedAt: z.iso.datetime().nullable(),
});
export type ServiceRequest = z.infer<typeof serviceRequestSchema>;

/**
 * `staff.ordering.listLive`: the Orders tab and the Tables tab. Open and
 * billed orders newest first; QR batches waiting for a decision oldest
 * first; requests not resolved, oldest first.
 */
export const liveBoardSchema = z.object({
	orders: z.array(liveOrderSchema),
	pendingBatches: z.array(batchSchema),
	serviceRequests: z.array(serviceRequestSchema),
});
export type LiveBoard = z.infer<typeof liveBoardSchema>;

// ---------------------------------------------------------------------------
// Inputs.
// ---------------------------------------------------------------------------

export const orderIdInputSchema = z.object({ orderId: z.uuid() });

/**
 * `staff.ordering.createOrder`. The client makes `id`; the same call again
 * returns the same order. Dine-in needs a free table. With `batch`, the first
 * items go to the kitchen in the same step.
 */
export const createOrderInputSchema = z
	.object({
		id: z.uuid(),
		type: orderTypeSchema,
		tableId: z.uuid().nullable().optional(),
		covers: z.number().int().min(1).max(100).nullable().optional(),
		customerName: optionalNote(60),
		customerPhone: phoneSchema.nullable().optional(),
		notes: optionalNote(200),
		batch: z.object({ clientRequestId: z.uuid(), lines, note: optionalNote(200) }).optional(),
	})
	.refine((o) => o.type === "takeaway" || Boolean(o.tableId), {
		message: "Dine-in needs a table",
		path: ["tableId"],
	})
	.refine((o) => o.type === "dine_in" || !o.tableId, {
		message: "A takeaway has no table",
		path: ["tableId"],
	});
export type CreateOrderInput = z.input<typeof createOrderInputSchema>;

/** `staff.ordering.addBatch`: send more items to the kitchen. Accepted at once. */
export const addBatchInputSchema = z.object({
	orderId: z.uuid(),
	clientRequestId: z.uuid(),
	lines,
	note: optionalNote(200),
});
export type AddBatchInput = z.input<typeof addBatchInputSchema>;

/** `staff.ordering.acceptBatch`, optionally taking out sold-out items. */
export const acceptBatchInputSchema = z.object({
	batchId: z.uuid(),
	removeItemIds: z.array(z.uuid()).max(100).default([]),
});
export type AcceptBatchInput = z.input<typeof acceptBatchInputSchema>;

/** `staff.ordering.rejectBatch`. The diner sees the reason. */
export const rejectBatchInputSchema = z.object({ batchId: z.uuid(), reason });
export type RejectBatchInput = z.input<typeof rejectBatchInputSchema>;

/**
 * `staff.ordering.cancelItem`. Without `quantity` the whole line; with it,
 * that many units (the rest stay). `clientRequestId` makes a retry safe.
 */
export const cancelItemInputSchema = z.object({
	orderItemId: z.uuid(),
	clientRequestId: z.uuid(),
	quantity: z.number().int().min(1).max(999).optional(),
	reason,
});
export type CancelItemInput = z.input<typeof cancelItemInputSchema>;

export const cancelOrderInputSchema = z.object({ orderId: z.uuid(), reason });
export type CancelOrderInput = z.input<typeof cancelOrderInputSchema>;

/** `staff.ordering.moveTable`: to a table with no open order. */
export const moveTableInputSchema = z.object({ orderId: z.uuid(), tableId: z.uuid() });

/** `staff.ordering.mergeOrders`: the source's items join the target; the source closes. */
export const mergeOrdersInputSchema = z
	.object({ sourceOrderId: z.uuid(), targetOrderId: z.uuid() })
	.refine((m) => m.sourceOrderId !== m.targetOrderId, {
		message: "Choose two different orders",
		path: ["targetOrderId"],
	});

export const listKotsInputSchema = z.object({
	status: kotStatusSchema.optional(),
	/** Newest first. */
	limit: z.number().int().min(1).max(100).default(50),
});

export const setKotStatusInputSchema = z.object({ kotId: z.uuid(), status: kotStatusSchema });

export const serviceRequestIdInputSchema = z.object({ id: z.uuid() });

/** `staff.ordering.blockGuest`: from the order card of one of the guest's batches. */
export const blockGuestInputSchema = z.object({ batchId: z.uuid(), blocked: z.boolean() });
export const blockGuestResultSchema = z.object({ batchId: z.uuid(), guest: batchGuestSchema });

// ---------------------------------------------------------------------------
// Ordering settings (MG-09).
// ---------------------------------------------------------------------------

export const orderingSettingsSchema = z.object({
	/** Diners can order from the table's QR code. */
	qrOrderingEnabled: z.boolean(),
	/** Diners' batches skip staff acceptance. */
	autoAcceptQrOrders: z.boolean(),
	/** Call waiter, water and bill buttons. */
	serviceRequestsEnabled: z.boolean(),
	/** Ask for a name and phone at checkout (always optional). */
	askGuestName: z.boolean(),
	/** The storefront menu starts with the most ordered dishes of the last 30 days (P11-T09). */
	showPopular: z.boolean(),
});
export type OrderingSettings = z.infer<typeof orderingSettingsSchema>;

export const updateOrderingSettingsInputSchema = orderingSettingsSchema.partial();
